import React from "react";
import { useCallback, useMemo, useState } from "react";
import { NodeDeleteDialog } from "../components/NodeDeleteDialog";

/** @typedef {import("../types").InfoNode} InfoNode */

/**
 * Encapsulates delete dialog state and actions.
 * @param {{
 *   getNodeById: (id: string) => InfoNode | undefined,
 *   onDelete: (node: InfoNode) => Promise<void>,
 * }} params
 */
export function useDeleteNode({ getNodeById, onDelete }) {
	const [pendingNode, setPendingNode] = useState(null);
	const [isDeleting, setIsDeleting] = useState(false);

	const promptDelete = useCallback(
		(RowId) => {
			const node = getNodeById(RowId);
			if (!node) {
				alert("Something went wrong with the request. Please try again.");
				return;
			}
			setPendingNode(node);
		},
		[getNodeById],
	);

	const cancelDelete = useCallback(() => {
		if (isDeleting) return;
		setPendingNode(null);
	}, [isDeleting]);

	const confirmDelete = useCallback(async () => {
		if (!pendingNode) return;
		setIsDeleting(true);
		try {
			await onDelete(pendingNode);
			setPendingNode(null);
		} catch (_err) {
			alert("Something went wrong with the request. Please try again.");
		} finally {
			setIsDeleting(false);
		}
	}, [pendingNode, onDelete]);

	const DeleteDialog = useMemo(
		() =>
			function DeleteDialogComponent() {
				return (
					<NodeDeleteDialog
						isOpen={Boolean(pendingNode)}
						isDeleting={isDeleting}
						node={pendingNode}
						onConfirm={confirmDelete}
						onCancel={cancelDelete}
					/>
				);
			},
		[pendingNode, isDeleting, confirmDelete, cancelDelete],
	);

	return {
		isDeleting,
		promptDelete,
		cancelDelete,
		confirmDelete,
		DeleteDialog,
	};
}
